'use client';

import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'motion/react';
import { 
  ChevronRight, Download, Smartphone, Tv, Monitor, ShieldCheck, 
  PlayCircle, CheckCircle2, MessageCircle, Zap, Star, LayoutGrid
} from 'lucide-react';

export default function ApplicationPage() {
  const apps = [
    {
      id: 1,
      name: 'ESIPTV PRO Player',
      platform: 'Android / TV / Fire TV',
      subtitle: 'L\'expérience Android ultime',
      description: 'Notre application phare conçue spécifiquement pour les appareils Android. Profitez d\'un streaming fluide, d\'une interface intuitive et de fonctionnalités avancées comme la Catch-up TV et le multi-écrans.',
      gradient: 'from-blue-500 to-cyan-400',
      image: '/img/ImageProduct_87133_675c7a056c116.jpg',
      version: 'v4.2.1',
      icon: Smartphone,
      features: ['Streaming 4K', 'Support EPG', 'Catch-up TV', 'Multi-écrans', 'Contrôle Parental', 'Zapping Rapide'],
      tags: ['Android TV', 'FireStick', 'Box Android', 'Smartphone']
    },
    {
      id: 2,
      name: 'ESIPTV Smarters Pro',
      platform: 'iOS / iPhone / iPad',
      subtitle: 'Le streaming premium sur appareils Apple',
      description: 'Une application magnifiquement conçue pour l\'écosystème Apple. Profitez de votre contenu préféré avec les performances natives d\'iOS, le support Picture-in-Picture et la synchronisation iCloud.',
      gradient: 'from-[#7aaace] to-[#1b3c53]',
      image: '/img/ImageProduct_87133_675c7a056c116.jpg',
      version: 'v3.0.5',
      icon: Smartphone,
      features: ['Picture-in-Picture', 'Support AirPlay', 'Synchro iCloud', 'Mode Hors-ligne', 'Mode Sombre', 'Widgets'],
      tags: ['iPhone', 'iPad', 'Apple TV', 'Mac (M1/M2)']
    },
    {
      id: 3,
      name: 'ESIPTV Smart Player',
      platform: 'Windows / Mac / Linux',
      subtitle: 'La puissance du divertissement sur bureau',
      description: 'Transformez votre ordinateur en un centre de divertissement premium. Notre lecteur de bureau offre des raccourcis clavier, le support multi-écrans et l\'accélération matérielle pour une lecture fluide.',
      gradient: 'from-emerald-500 to-teal-400',
      image: '/img/ImageProduct_87133_675c7a056c116.jpg',
      version: 'v2.1.0',
      icon: Monitor,
      features: ['Accélération Matérielle', 'Raccourcis Clavier', 'Multi-écrans', 'Enregistrement', 'Bibliothèque VOD', 'Thèmes Personnalisés'],
      tags: ['Windows 10/11', 'macOS', 'Ubuntu', 'Navigateur Web']
    },
    {
      id: 4,
      name: 'ESIPTV Smart TV',
      platform: 'Samsung / LG / Philips',
      subtitle: 'Intégration native Smart TV',
      description: 'Aucun boîtier supplémentaire nécessaire. Installez directement sur votre Smart TV pour l\'expérience la plus intégrée. Contrôlez tout avec votre télécommande standard.',
      gradient: 'from-orange-500 to-red-500',
      image: '/img/ImageProduct_87133_675c7a056c116.jpg',
      version: 'v1.5.2',
      icon: Tv,
      features: ['Interface Native', 'Télécommande', 'Recherche Vocale', 'Démarrage Auto', 'Faible Latence', 'Mise à jour Auto'],
      tags: ['Tizen OS', 'WebOS', 'Android TV', 'Vidaa']
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-8">
            <Link href="/" className="hover:text-white transition-colors">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Application</span>
          </div>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-blue-400 text-sm font-medium mb-6">
              <Download className="w-4 h-4" /> Téléchargements Gratuits
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">Nos </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7aaace] to-[#1b3c53]">Applications</span>
            </h1>
            <p className="text-xl text-gray-400 mb-12 leading-relaxed">
              Téléchargez nos applications officielles pour profiter de la meilleure expérience IPTV sur tous vos appareils. Rapide, sécurisé et 100% gratuit.
            </p>

            {/* 4-stat grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Applications', value: '4', icon: LayoutGrid },
                { label: 'Appareils', value: '10+', icon: Smartphone },
                { label: 'Qualité Max', value: '4K', icon: Star },
                { label: 'Gratuit', value: '100%', icon: ShieldCheck }
              ].map((stat, i) => (
                <div key={i} className="bg-[#111] border border-white/5 rounded-2xl p-4 flex flex-col items-center justify-center text-center">
                  <stat.icon className="w-6 h-6 text-blue-400 mb-2" />
                  <span className="text-2xl font-bold text-white">{stat.value}</span>
                  <span className="text-xs text-gray-400 uppercase tracking-wider">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* App Cards Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-32">
          {apps.map((app, index) => (
            <div key={app.id} className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-20 items-center`}>
              {/* Image Side */}
              <motion.div 
                initial={{ opacity: 0, x: index % 2 === 1 ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="w-full lg:w-1/2 relative"
              >
                <div className={`absolute inset-0 bg-gradient-to-tr ${app.gradient} blur-3xl opacity-20 rounded-full`}></div>
                <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-[#111]">
                  <Image src={app.image} alt={app.name} fill className="object-cover opacity-80" referrerPolicy="no-referrer" />
                  <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-md border border-white/10 px-3 py-1 rounded-full text-xs font-bold text-white">
                    {app.version}
                  </div>
                </div>
              </motion.div>

              {/* Content Side */}
              <motion.div 
                initial={{ opacity: 0, x: index % 2 === 1 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="w-full lg:w-1/2 space-y-8"
              >
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${app.gradient} p-[1px]`}>
                      <div className="w-full h-full bg-[#111] rounded-xl flex items-center justify-center">
                        <app.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-white">{app.name}</h2>
                      <p className="text-gray-400">{app.platform}</p>
                    </div>
                  </div>
                  <h3 className="text-xl font-medium text-white mb-4">{app.subtitle}</h3>
                  <p className="text-gray-400 leading-relaxed">{app.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {app.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
                      <span className="text-sm text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2">
                  {app.tags.map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-gray-400">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-r ${app.gradient} text-white font-bold hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all hover:-translate-y-1`}>
                    <Download className="w-5 h-5" /> Télécharger
                  </button>
                  <a href="https://wa.me/33745561646" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-[#111] border border-white/10 text-white font-medium hover:bg-[#222] transition-colors">
                    <MessageCircle className="w-5 h-5" /> Aide
                  </a>
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      {/* Installation Steps */}
      <section className="py-24 bg-[#0f0f0f] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Comment ça marche ?</h2>
            <p className="text-gray-400">Trois étapes simples pour commencer à regarder.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-1/2 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-blue-500/0 via-blue-500/50 to-blue-500/0 -translate-y-1/2 z-0"></div>
            
            {[
              { step: '1', title: 'Téléchargez', desc: 'Choisissez l\'application correspondante à votre appareil.', icon: Download, gradient: 'from-blue-500 to-cyan-400' },
              { step: '2', title: 'Activez', desc: 'Entrez vos identifiants reçus par email après l\'achat.', icon: Zap, gradient: 'from-[#7aaace] to-[#1b3c53]' },
              { step: '3', title: 'Regardez', desc: 'Profitez de vos chaînes et VOD en qualité 4K.', icon: PlayCircle, gradient: 'from-emerald-500 to-teal-400' },
            ].map((item, i) => (
              <div key={i} className="relative z-10 flex flex-col items-center text-center p-8 bg-[#111] rounded-3xl border border-white/5 hover:border-white/10 transition-colors">
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${item.gradient} p-[1px] mb-6`}>
                  <div className="w-full h-full bg-[#111] rounded-2xl flex items-center justify-center relative">
                    <item.icon className="w-8 h-8 text-white" />
                    <div className={`absolute -top-3 -right-3 w-8 h-8 rounded-full bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white font-bold border-4 border-[#111]`}>
                      {item.step}
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Banner */}
      <section className="py-12 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Applications sécurisées et vérifiées</h3>
                <p className="text-sm text-green-400">Garanties sans virus ni malware.</p>
              </div>
            </div>
            <Link href="/tutorials" className="px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-medium rounded-xl border border-white/10 transition-colors whitespace-nowrap">
              Voir les tutoriels
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-[2.5rem] p-12 text-center border border-white/10 relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Prêt à commencer ?</h2>
              <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
                Choisissez un abonnement et profitez de la meilleure qualité IPTV sur le marché.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link href="/#subscriptions" className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all hover:shadow-[0_0_20px_rgba(37,99,235,0.4)]">
                  Voir les abonnements
                </Link>
                <a href="https://wa.me/33745561646" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-xl transition-all hover:shadow-[0_0_20px_rgba(37,211,102,0.4)] flex items-center justify-center gap-2">
                  <MessageCircle className="w-5 h-5" /> Commander via WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
